import React, { useState, useEffect } from 'react';
import { FaUser, FaCamera, FaPlus, FaTrash, FaSave, FaCheckCircle, FaWhatsapp, FaTrophy, FaBriefcase } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';

import * as authService from '../services/authService';
import * as profileService from '../services/profileService';
import * as masterService from '../services/masterService';
import { API_BASE_URL } from '../config';

const Profile = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null); // URL from API
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(false);
  const [whatsappSameAsAltPhone, setWhatsappSameAsAltPhone] = useState(false);
  const [collegeSameAsUniversity, setCollegeSameAsUniversity] = useState(false);
  const [isRetiredProfessor, setIsRetiredProfessor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState('user'); // Default to user
  const [isReviewer, setIsReviewer] = useState(false);
  const [isEditorial, setIsEditorial] = useState(false);

  // Master data from localStorage
  const [masterSubjects, setMasterSubjects] = useState([]);
  const [masterMajors, setMasterMajors] = useState([]);
  const [masterSpecializations, setMasterSpecializations] = useState([]);

  // Filtered master data for dropdowns
  const [filteredMajors, setFilteredMajors] = useState([]);
  const [filteredSpecializations, setFilteredSpecializations] = useState([]);

  // Document URLs from API
  const [documentUrls, setDocumentUrls] = useState({
    profileImageUrl: '',
    aadhaarFrontUrl: '',
    aadhaarBackUrl: '',
  });
  const [selectedPortalSubjects, setSelectedPortalSubjects] = useState([]);
  const [selectedPortalStream, setSelectedPortalStream] = useState([]);
  const [selectedPortalSpecialization, setSelectedPortalSpecialization] = useState([]);

  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    dateOfBirth: '',
    phoneNumber: '',
    altPhoneNumber: '',
    whatsappNumber: '',
    email: '',
    altEmail: '',

    // Current Address
    currentAddressLine1: '',
    currentAddressLine2: '',
    currentCity: '',
    currentState: '',
    currentPinCode: '',
    currentCountry: '',

    // Permanent Address
    permanentAddressLine1: '',
    permanentAddressLine2: '',
    permanentCity: '',
    permanentState: '',
    permanentPinCode: '',
    permanentCountry: '',

    // Academic Information - Common fields
    university: '',
    collegeName: '',
    stream: '',
    subject: '',
    major: '',
    specialization: '',
    session: '',
    startingYear: '',
    expectedCompletionYear: '',
    presentResearchArea: '', // New field for professors

    // Professor/Admin specific fields
    facultyDepartment: '',
    designation: '',
    dateOfJoining: '',
    retirementDate: '',

    // Professor Achievement fields
    awardCertificate: '',
    awardYear: '',
    awardedBy: '',
    achievementNote: '',

    // Admin/User Designation fields
    portalDesignation: '',
    portalJoiningDate: '',
    lastCompany: '',
    totalExperience: '',

    // Banking Information
    reviewConsultancyCharges: '',
    bankName: '',
    ifscCode: '',
    accountNumber: '',
    branch: '',
    upiId: '',
    phonePayNumber: '',
    gPayNumber: '',

    // Salary Structure (for employees/users - only visible when admin fills)
    basicSalary: '',
    hra: '',
    da: '',
    perksFacilities: '',
    ctc: '',
    deduction: '',
    pf: '',
    medical: '',
    li: '',
    advancedItDeduction: '',

    // Aadhaar Information
    aadhaarNumber: '',
  });

  const [salaryStructureFilled, setSalaryStructureFilled] = useState(false);

  const [educationEntries, setEducationEntries] = useState([
    {
      id: 1,
      lastQualification: '',
      boardUniversity: '',
      instituteName: '',
      passingYear: '',
      marksGrade: '',
      certificate: null,
      marksheet: null,
      certificateUrl: '',
      marksheetUrl: '',
    },
  ]);

  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);



  const [searchUniversity, setSearchUniversity] = useState('');
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [universityList, setUniversityList] = useState([]);

  const [searchSubject, setSearchSubject] = useState('');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);

  const [searchStream, setSearchStream] = useState('');
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);

  const [searchSpecialization, setSearchSpecialization] = useState('');
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);

  // Load master data and saved profile data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);

        // Fetch all master data and profile data in parallel
        const [subjectsResponse, streamsResponse, specsResponse, profileResponse, universitiesResponse] = await Promise.all([
          masterService.getAllSubjects(),
          masterService.getAllStreams(),
          masterService.getAllSpecializations(),
          profileService.getProfile(),
          masterService.getAllUniversities()
        ]);

        // Process Master Data
        const subjectsArray = Array.isArray(subjectsResponse) ? subjectsResponse : (subjectsResponse?.data || subjectsResponse?.subjects || []);
        const streamsArray = Array.isArray(streamsResponse) ? streamsResponse : (streamsResponse?.data || streamsResponse?.streams || []);
        const specializationsArray = Array.isArray(specsResponse) ? specsResponse : (specsResponse?.data || specsResponse?.specializations || []);

        // Process University Data
        let univArray = [];
        if (universitiesResponse.success && Array.isArray(universitiesResponse.data)) {
          univArray = universitiesResponse.data;
        } else if (Array.isArray(universitiesResponse)) {
          univArray = universitiesResponse;
        }
        setUniversityList(univArray.slice(0, 50)); // Limit initial load

        setMasterSubjects(subjectsArray);
        setMasterMajors(streamsArray);
        setMasterSpecializations(specializationsArray);

        // Process Profile Data
        if (profileResponse.success && profileResponse.data) {
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            setCurrentUserId(currentUser.id);
            setUserRole(currentUser.role || 'user');
            setIsReviewer(!!currentUser.is_reviewer || currentUser.is_reviewer === 1);
            setIsEditorial(!!currentUser.is_editorial_board || currentUser.is_editorial_board === 1 || !!currentUser.is_verified_sub_eb || currentUser.is_verified_sub_eb === 1);
          }

          const mapped = profileService.mapApiToFormState(profileResponse.data);

          // Set form data
          setFormData(prev => ({ ...prev, ...mapped.formData }));

          // Pre-populate search fields with existing values
          if (mapped.formData.university) setSearchUniversity(mapped.formData.university);

          if (mapped.formData.subject) {
            const sub = subjectsArray.find(s => String(s.id) === String(mapped.formData.subject));
            if (sub) setSearchSubject(sub.name);
          }
          if (mapped.formData.stream) {
            const str = streamsArray.find(s => String(s.id) === String(mapped.formData.stream));
            if (str) setSearchStream(str.name);
          }
          if (mapped.formData.specialization) {
            const spec = specializationsArray.find(s => String(s.id) === String(mapped.formData.specialization));
            if (spec) setSearchSpecialization(spec.name);
          }

          // Set document URLs
          setDocumentUrls({
            profileImageUrl: mapped.profileImageUrl || '',
            aadhaarFrontUrl: mapped.aadhaarFrontUrl || '',
            aadhaarBackUrl: mapped.aadhaarBackUrl || '',
          });

          if (mapped.profileImageUrl) {
            setProfileImageUrl(mapped.profileImageUrl);
            setProfileImage(null); // Clear preview to show server-saved image
          }

          // Initial filtering for dynamic dropdowns
          if (mapped.formData.subject) {
            const subjectId = mapped.formData.subject;
            const filteredM = streamsArray.filter(major =>
              String(major.subject_id) === String(subjectId) ||
              String(major.subjectId) === String(subjectId)
            );
            setFilteredMajors(filteredM);

            if (mapped.formData.stream) {
              const streamId = mapped.formData.stream;
              const filteredS = specializationsArray.filter(spec =>
                String(spec.stream_id) === String(streamId) ||
                String(spec.streamId) === String(streamId)
              );
              setFilteredSpecializations(filteredS);
            }
          }



          // Set education history
          if (mapped.educationHistory && mapped.educationHistory.length > 0) {
            setEducationEntries(mapped.educationHistory.map((entry, index) => ({
              id: index + 1,
              lastQualification: entry.qualification || '',
              boardUniversity: entry.board_university || '',
              instituteName: entry.institute_name || '',
              passingYear: entry.passing_year || '',
              marksGrade: entry.marks_grade || '',
              certificate: null,
              marksheet: null,
              certificateUrl: entry.certificate_url || '',
              marksheetUrl: entry.marksheet_url || '',
            })));
          }

          // Set retired status
          if (mapped.isRetired) setIsRetiredProfessor(true);

          // Set portal preference
          setSelectedPortalSubjects(mapped.selectedPortalSubjects || []);
          setSelectedPortalStream(mapped.selectedPortalStream || []);
          setSelectedPortalSpecialization(mapped.selectedPortalSpecializations || []);

          // Address consistency
          const isSame =
            mapped.formData.currentAddressLine1 === mapped.formData.permanentAddressLine1 &&
            mapped.formData.currentAddressLine2 === mapped.formData.permanentAddressLine2 &&
            mapped.formData.currentCity === mapped.formData.permanentCity &&
            mapped.formData.currentState === mapped.formData.permanentState &&
            mapped.formData.currentPinCode === mapped.formData.permanentPinCode &&
            mapped.formData.currentCountry === mapped.formData.permanentCountry &&
            mapped.formData.currentAddressLine1;

          if (isSame) setSameAsPermanent(true);

          // WhatsApp & College logic
          if (mapped.formData.whatsappNumber && mapped.formData.whatsappNumber === mapped.formData.phoneNumber) setWhatsappSameAsPhone(true);
          else if (mapped.formData.whatsappNumber && mapped.formData.whatsappNumber === mapped.formData.altPhoneNumber) setWhatsappSameAsAltPhone(true);
          if (mapped.formData.collegeName && mapped.formData.collegeName === mapped.formData.university) setCollegeSameAsUniversity(true);

          if (mapped.formData.basicSalary || mapped.formData.ctc || mapped.formData.hra) setSalaryStructureFilled(true);

          showToast('Profile loaded successfully', 'success');
        }
      } catch (error) {
        console.error('Error loading initial profile data:', error);
        if (!error.message?.includes('404')) {
          showToast('Could not load profile. Please try again.', 'error');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [navigate]);

  // Debounced Search Effects
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchUniversity && searchUniversity.length >= 2) {
        // Don't search if the search text matches the selected university exactly
        if (formData.university === searchUniversity) return;

        try {
          const response = await masterService.searchUniversities(searchUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }

          // Add "Other" to results
          if (!results.some(u => (u.name || u) === "Other")) {
            results.push({ name: "Other", state: "" });
          }
          setUniversityList(results);
          setShowUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      } else if (!searchUniversity) {
        // Reset to initial list when cleared
        try {
          const response = await masterService.getAllUniversities();
          let univArray = [];
          if (response.success && Array.isArray(response.data)) {
            univArray = response.data;
          } else if (Array.isArray(response)) {
            univArray = response;
          }
          setUniversityList(univArray.slice(0, 50));
        } catch (e) { console.error(e); }
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchUniversity, formData.university]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const getImageUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmedUrl = url.trim();
    if (trimmedUrl.startsWith('data:') || trimmedUrl.startsWith('http')) return trimmedUrl;
    const serverUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
    const cleanUrl = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    return `${serverUrl}${cleanUrl}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Create new form data
    let newFormData = { ...formData, [name]: value };

    // Handle dynamic filtering logic
    if (name === 'subject') {
      // If subject (Department) changes, reset and filter streams
      const subjectId = value;
      const filtered = masterMajors.filter(major =>
        String(major.subject_id) === String(subjectId) ||
        String(major.subjectId) === String(subjectId)
      );
      setFilteredMajors(filtered);

      // Reset dependent fields
      newFormData.stream = '';
      newFormData.specialization = '';
      setFilteredSpecializations([]);
    }
    else if (name === 'stream') {
      // If stream changes, reset and filter specializations
      const streamId = value;
      const filtered = masterSpecializations.filter(spec =>
        String(spec.stream_id) === String(streamId) ||
        String(spec.streamId) === String(streamId)
      );
      setFilteredSpecializations(filtered);

      // Reset dependent field
      newFormData.specialization = '';
    }

    setFormData(newFormData);
  };



  const handleSameAsPermanent = (e) => {
    const checked = e.target.checked;
    setSameAsPermanent(checked);

    if (checked) {
      setFormData({
        ...formData,
        permanentAddressLine1: formData.currentAddressLine1,
        permanentAddressLine2: formData.currentAddressLine2,
        permanentCity: formData.currentCity,
        permanentState: formData.currentState,
        permanentPinCode: formData.currentPinCode,
        permanentCountry: formData.currentCountry,
      });
    }
  };

  const handleWhatsappSameAsPhone = (e) => {
    const checked = e.target.checked;
    setWhatsappSameAsPhone(checked);
    if (checked) {
      setWhatsappSameAsAltPhone(false);
      setFormData({
        ...formData,
        whatsappNumber: formData.phoneNumber
      });
    }
  };



  const handleCollegeSameAsUniversity = (e) => {
    const checked = e.target.checked;
    setCollegeSameAsUniversity(checked);
    if (checked) {
      setFormData({
        ...formData,
        collegeName: formData.university
      });
    }
  };

  const handleRetiredProfessor = (e) => {
    const checked = e.target.checked;
    setIsRetiredProfessor(checked);
    if (!checked) {
      setFormData({
        ...formData,
        retirementDate: ''
      });
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file); // Store file for upload API
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result); // Data URL for preview
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEducationChange = (id, field, value) => {
    setEducationEntries(
      educationEntries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleEducationFileChange = (id, field, file) => {
    setEducationEntries(
      educationEntries.map((entry) =>
        entry.id === id ? { ...entry, [field]: file } : entry
      )
    );
  };

  const addEducationEntry = () => {
    const newId = Math.max(...educationEntries.map((e) => e.id)) + 1;
    setEducationEntries([
      ...educationEntries,
      {
        id: newId,
        lastQualification: '',
        boardUniversity: '',
        instituteName: '',
        passingYear: '',
        marksGrade: '',
        certificate: null,
        marksheet: null,
      },
    ]);
  };

  const removeEducationEntry = (id) => {
    if (educationEntries.length > 1) {
      setEducationEntries(educationEntries.filter((entry) => entry.id !== id));
    } else {
      showToast('At least one education entry is required', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUserId) {
      showToast('Please login to save your profile', 'error');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    setIsSaving(true);

    try {
      let updatedDocumentUrls = { ...documentUrls };
      let updatedEducationEntries = [...educationEntries];

      // 1. Upload Profile Image
      if (profileImageFile) {
        showToast('Uploading profile picture...', 'info');
        try {
          const uploadRes = await profileService.uploadFile(profileImageFile);
          if (uploadRes.success) {
            updatedDocumentUrls.profileImageUrl = uploadRes.data?.file_url;
          }
        } catch (err) {
          console.error('Profile image upload failed:', err);
        }
      }

      // 2. Upload Aadhaar Front
      if (aadhaarFront && aadhaarFront instanceof File) {
        showToast('Uploading Aadhaar front...', 'info');
        try {
          const uploadRes = await profileService.uploadFile(aadhaarFront);
          if (uploadRes.success) {
            updatedDocumentUrls.aadhaarFrontUrl = uploadRes.data?.file_url;
          }
        } catch (err) {
          console.error('Aadhaar front upload failed:', err);
        }
      }

      // 3. Upload Aadhaar Back
      if (aadhaarBack && aadhaarBack instanceof File) {
        showToast('Uploading Aadhaar back...', 'info');
        try {
          const uploadRes = await profileService.uploadFile(aadhaarBack);
          if (uploadRes.success) {
            updatedDocumentUrls.aadhaarBackUrl = uploadRes.data?.file_url;
          }
        } catch (err) {
          console.error('Aadhaar back upload failed:', err);
        }
      }

      // 4. Upload Education Files
      for (let i = 0; i < updatedEducationEntries.length; i++) {
        const entry = updatedEducationEntries[i];
        if (entry.certificate && entry.certificate instanceof File) {
          showToast(`Uploading certificate ${i + 1}...`, 'info');
          try {
            const res = await profileService.uploadFile(entry.certificate);
            if (res.success) {
              updatedEducationEntries[i].certificateUrl = res.data?.file_url;
            }
          } catch (err) { console.error(err); }
        }
        if (entry.marksheet && entry.marksheet instanceof File) {
          showToast(`Uploading marksheet ${i + 1}...`, 'info');
          try {
            const res = await profileService.uploadFile(entry.marksheet);
            if (res.success) {
              updatedEducationEntries[i].marksheetUrl = res.data?.file_url;
            }
          } catch (err) { console.error(err); }
        }
      }

      // Map form state to API payload
      const apiPayload = profileService.mapFormStateToApi(
        formData,
        userRole,
        updatedEducationEntries,
        selectedPortalSubjects,
        selectedPortalStream,
        selectedPortalSpecialization,
        updatedDocumentUrls
      );

      console.log('Final Save Payload:', apiPayload);

      // Save to API
      const response = await profileService.updateProfile(apiPayload);

      if (response.success) {
        showToast('Profile saved successfully!', 'success');
        setDocumentUrls(updatedDocumentUrls);
        setProfileImageUrl(updatedDocumentUrls.profileImageUrl);
        setProfileImageFile(null);
        setAadhaarFront(null);
        setAadhaarBack(null);
        setEducationEntries([...updatedEducationEntries]);
      } else {
        showToast(response.message || 'Failed to save profile', 'error');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showToast(error.message || 'Error saving profile. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all hover:border-gray-300 text-sm";
  const labelClass = "block text-xs font-semibold text-gray-700 mb-1";
  const sectionBadgeClass = "w-9 h-9 rounded-xl flex items-center justify-center text-base font-bold shadow-lg text-white";
  const checkboxClass = "w-4 h-4 text-primary-blue focus:ring-primary-blue border-gray-300 rounded";

  const isProfessorOnly = userRole === 'professor';
  const isAcademicRole = (userRole === 'professor' || isReviewer || isEditorial);

  // Conditionally show Banking/Achievement only if professor is assigned as reviewer or EB
  const showBankingAndAchievement = isAcademicRole || userRole === 'admin' || userRole === 'user';

  // Determine grid columns for professors/admins
  const professorGridCols = isProfessorOnly && isRetiredProfessor ? 'md:grid-cols-5' : 'md:grid-cols-4';

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  // Dynamic Multi-section Numbering logic
  let sectionCounter = 0;
  const getNextSectionNumber = () => {
    sectionCounter++;
    return sectionCounter;
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-3">
        <div className="max-w mx-auto px-4">
          {/* Compact Header */}
          <div className="rounded-2xl shadow-lg p-4 mb-3 relative overflow-hidden" style={darkGradient}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white">My Profile</h1>
                <p className="text-blue-100 text-sm">
                  {isLoading ? 'Loading profile...' : 'Complete and update your profile'}
                </p>
                <p className="text-blue-200 text-xs mt-1">
                  <span className="text-red-300">*</span> Only Personal Info, Address & Academic Info are mandatory
                </p>
              </div>
              <div className="hidden md:block">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaUser className="text-2xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue mb-4"></div>
                <p className="text-gray-600">Loading your profile...</p>
              </div>
            </div>
          ) : (
            /* Compact Form - Original Design */
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <form onSubmit={handleSubmit}>

                {/* Personal Information - MANDATORY */}
                <div className="p-4 border-b-2 border-gray-100 bg-gradient-to-br from-red-50/20 to-pink-50/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Personal Information
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">MANDATORY</span>
                      </h2>
                      <p className="text-gray-500 text-xs">Tell us about yourself</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-7 gap-3 mb-3">
                    <div>
                      <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        placeholder="Full name"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Date of Birth <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Phone Number <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={(e) => {
                          handleChange(e);
                          if (whatsappSameAsPhone) {
                            setFormData(prev => ({ ...prev, whatsappNumber: e.target.value }));
                          }
                        }}
                        maxLength="10"
                        required
                        className={inputClass}
                        placeholder="Phone"
                      />
                      <div className="mt-1">
                        <label className="flex items-center space-x-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={whatsappSameAsPhone}
                            onChange={handleWhatsappSameAsPhone}
                            className="w-3 h-3"
                          />
                          <span className="text-[10px] text-gray-500 leading-none">
                            WA Same
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Alt Phone</label>
                      <input
                        type="tel"
                        name="altPhoneNumber"
                        value={formData.altPhoneNumber}
                        onChange={(e) => {
                          handleChange(e);
                          if (whatsappSameAsAltPhone) {
                            setFormData(prev => ({ ...prev, whatsappNumber: e.target.value }));
                          }
                        }}
                        maxLength="10"
                        className={inputClass}
                        placeholder="Alt phone"
                      />
                    </div>

                    <div>
                      <label className={`${labelClass} flex items-center gap-1`}>
                        <FaWhatsapp className="text-green-500 text-sm" />
                        <span>WA Number</span>
                      </label>
                      <input
                        type="tel"
                        name="whatsappNumber"
                        value={formData.whatsappNumber}
                        onChange={handleChange}
                        maxLength="10"
                        className={inputClass}
                        placeholder="WhatsApp"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Email ID <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        placeholder="Email"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Alt Email</label>
                      <input
                        type="email"
                        name="altEmail"
                        value={formData.altEmail}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Alt email"
                      />
                    </div>
                  </div>

                </div>

                {/* Academic Information - MANDATORY */}
                <div className="p-4 border-b-2 border-gray-100 bg-gradient-to-br from-red-50/20 to-pink-50/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          {isProfessorOnly && isRetiredProfessor ? 'Last Academic Information' : 'Current Academic Information'}
                          {!['admin', 'user'].includes(userRole) && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">MANDATORY</span>
                          )}
                        </h2>
                        <p className="text-gray-500 text-xs">Your educational background</p>
                      </div>
                    </div>

                    {/* Retired Professor Checkbox - Now moved here */}
                    {isProfessorOnly && (
                      <label className="flex items-center space-x-2 cursor-pointer bg-gradient-to-r from-yellow-50 to-orange-50 px-4 py-2 rounded-xl shadow-sm border border-orange-200">
                        <input
                          type="checkbox"
                          checked={isRetiredProfessor}
                          onChange={handleRetiredProfessor}
                          className={checkboxClass}
                        />
                        <span className="text-xs font-semibold text-gray-700">
                          Retired Professor
                        </span>
                      </label>
                    )}
                  </div>

                  {/* For Professors/Admins */}
                  {['admin', 'user'].includes(userRole) ? (
                    <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 italic text-sm">
                      Academic information is not required for your role.
                    </div>
                  ) : isAcademicRole ? (
                    <div className={`grid grid-cols-1 ${professorGridCols} gap-3 mb-3`}>
                      {/* University - Column 1 */}
                      <div className="relative">
                        <label className={labelClass}>Present / Last University <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          placeholder="Search & Select University"
                          value={formData.university || searchUniversity}
                          onChange={(e) => {
                            setSearchUniversity(e.target.value);
                            setFormData({ ...formData, university: '' });
                            setShowUniversityDropdown(true);
                          }}
                          onFocus={() => setShowUniversityDropdown(true)}
                          onBlur={() => setTimeout(() => setShowUniversityDropdown(false), 200)}
                          className={`${inputClass} bg-white`}
                          required
                        />
                        {showUniversityDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {universityList.length > 0 ? (
                              universityList.map((uni, idx) => (
                                <div
                                  key={idx}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                  onMouseDown={() => {
                                    const name = uni.name || uni;
                                    setFormData({ ...formData, university: name });
                                    setSearchUniversity(name);
                                    setShowUniversityDropdown(false);
                                  }}
                                >
                                  {uni.name || uni}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                                onMouseDown={() => {
                                  setFormData({ ...formData, university: 'Other' });
                                  setSearchUniversity('Other');
                                  setShowUniversityDropdown(false);
                                }}
                              >
                                Other (Type manually)
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* College Name - Column 2 */}
                      <div>
                        <label className={labelClass}>College Name <span className="text-gray-400 font-normal">(Optional)</span></label>
                        <input
                          type="text"
                          name="collegeName"
                          value={formData.collegeName}
                          onChange={handleChange}
                          disabled={collegeSameAsUniversity}
                          className={`${inputClass} ${collegeSameAsUniversity ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                          placeholder="College"
                        />
                        <div className="mt-1">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={collegeSameAsUniversity}
                              onChange={handleCollegeSameAsUniversity}
                              className="w-3 h-3"
                            />
                            <span className="text-xs text-gray-600">
                              Same as University
                            </span>
                          </label>
                        </div>
                      </div>



                      {/* Designation - Column 3 */}
                      <div>
                        <label className={labelClass}>Designation <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="designation"
                          value={formData.designation}
                          onChange={handleChange}
                          required
                          className={inputClass}
                          placeholder="e.g., Professor, Associate Professor" />
                      </div>

                      {/* Date of Joining - Column 4 (Always visible) */}
                      <div>
                        <label className={labelClass}>
                          {isProfessorOnly && isRetiredProfessor ? 'Date of Joining (Previous)' : 'Date of Joining'} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="dateOfJoining"
                          value={formData.dateOfJoining}
                          onChange={handleChange}
                          required
                          className={inputClass}
                          placeholder="Date of Joining"
                        />
                      </div>

                      {/* Retirement Date - Column 5 (Only when Retired Professor checked) */}
                      {isProfessorOnly && isRetiredProfessor && (
                        <div>
                          <label className={labelClass}>Retirement Date</label>
                          <input
                            type="date"
                            name="retirementDate"
                            value={formData.retirementDate}
                            onChange={handleChange}
                            className={inputClass}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    /* For Students - First Row: University, College, Stream, Subject (4 columns) */
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                        {/* University - Column 1 */}
                        <div className="relative">
                          <label className={labelClass}>Present / Last University <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            placeholder="Search & Select University"
                            value={formData.university || searchUniversity}
                            onChange={(e) => {
                              setSearchUniversity(e.target.value);
                              setFormData({ ...formData, university: '' });
                              setShowUniversityDropdown(true);
                            }}
                            onFocus={() => setShowUniversityDropdown(true)}
                            onBlur={() => setTimeout(() => setShowUniversityDropdown(false), 200)}
                            className={`${inputClass} bg-white`}
                            required
                          />
                          {showUniversityDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {universityList.length > 0 ? (
                                universityList.map((uni, idx) => (
                                  <div
                                    key={idx}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                    onMouseDown={() => {
                                      const name = uni.name || uni;
                                      setFormData({ ...formData, university: name });
                                      setSearchUniversity(name);
                                      setShowUniversityDropdown(false);
                                    }}
                                  >
                                    {uni.name || uni}
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                                  onMouseDown={() => {
                                    setFormData({ ...formData, university: 'Other' });
                                    setSearchUniversity('Other');
                                    setShowUniversityDropdown(false);
                                  }}
                                >
                                  Other (Type manually)
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* College Name - Column 2 */}
                        <div>
                          <label className={labelClass}>College Name <span className="text-gray-400 font-normal">(Optional)</span></label>
                          <input
                            type="text"
                            name="collegeName"
                            value={formData.collegeName}
                            onChange={handleChange}
                            disabled={collegeSameAsUniversity}
                            className={`${inputClass} ${collegeSameAsUniversity ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                            placeholder="College"
                          />
                          <div className="mt-1">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={collegeSameAsUniversity}
                                onChange={handleCollegeSameAsUniversity}
                                className="w-3 h-3"
                              />
                              <span className="text-xs text-gray-600">
                                Same as University
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* Department - Column 3 */}
                        <div className="relative">
                          <label className={labelClass}>Department <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={searchSubject}
                            onChange={(e) => {
                              setSearchSubject(e.target.value);
                              setShowSubjectDropdown(true);
                            }}
                            onFocus={() => setShowSubjectDropdown(true)}
                            onBlur={() => setTimeout(() => setShowSubjectDropdown(false), 200)}
                            className={`${inputClass} bg-white`}
                            placeholder="Search Department"
                          />
                          {showSubjectDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {masterSubjects.filter(u => u.name.toLowerCase().includes(searchSubject.toLowerCase())).map((sub) => (
                                <div
                                  key={sub.id}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                  onMouseDown={() => {
                                    setFormData({
                                      ...formData,
                                      subject: sub.id,
                                      stream: '',
                                      specialization: ''
                                    });
                                    setSearchSubject(sub.name);
                                    setSearchStream('');
                                    setSearchSpecialization('');
                                    // Filter streams
                                    const filteredM = masterMajors.filter(m => String(m.subject_id) === String(sub.id));
                                    setFilteredMajors(filteredM);
                                    setFilteredSpecializations([]);
                                    setShowSubjectDropdown(false);
                                  }}
                                >
                                  {sub.name}
                                </div>
                              ))}
                              {masterSubjects.length === 0 && <div className="px-3 py-2 text-gray-400 text-sm">No results</div>}
                            </div>
                          )}
                        </div>

                        {/* Stream - Column 4 */}
                        <div className="relative">
                          <label className={labelClass}>Stream <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={searchStream}
                            onChange={(e) => {
                              setSearchStream(e.target.value);
                              setShowStreamDropdown(true);
                            }}
                            onFocus={() => setShowStreamDropdown(true)}
                            onBlur={() => setTimeout(() => setShowStreamDropdown(false), 200)}
                            disabled={!formData.subject}
                            className={`${inputClass} bg-white disabled:bg-gray-50`}
                            placeholder="Search Stream"
                          />
                          {showStreamDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {filteredMajors.filter(u => u.name.toLowerCase().includes(searchStream.toLowerCase())).map((str) => (
                                <div
                                  key={str.id}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                  onMouseDown={() => {
                                    setFormData({
                                      ...formData,
                                      stream: str.id,
                                      specialization: ''
                                    });
                                    setSearchStream(str.name);
                                    setSearchSpecialization('');

                                    // Filter specs
                                    const filteredS = masterSpecializations.filter(s => String(s.stream_id) === String(str.id));
                                    setFilteredSpecializations(filteredS);

                                    setShowStreamDropdown(false);
                                  }}
                                >
                                  {str.name}
                                </div>
                              ))}
                              {filteredMajors.length === 0 && <div className="px-3 py-2 text-gray-400 text-sm">{!formData.subject ? 'Select Dept first' : 'No results'}</div>}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Second Row for Students */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        {/* Specialization - Column 1 */}
                        <div className="relative">
                          <label className={labelClass}>Specialization</label>
                          <input
                            type="text"
                            value={searchSpecialization}
                            onChange={(e) => {
                              setSearchSpecialization(e.target.value);
                              setShowSpecializationDropdown(true);
                            }}
                            onFocus={() => setShowSpecializationDropdown(true)}
                            onBlur={() => setTimeout(() => setShowSpecializationDropdown(false), 200)}
                            disabled={!formData.stream}
                            className={`${inputClass} bg-white disabled:bg-gray-50`}
                            placeholder="Search Specialization"
                          />
                          {showSpecializationDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {filteredSpecializations.filter(u => u.name.toLowerCase().includes(searchSpecialization.toLowerCase())).map((spec) => (
                                <div
                                  key={spec.id}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                  onMouseDown={() => {
                                    setFormData({ ...formData, specialization: spec.id });
                                    setSearchSpecialization(spec.name);
                                    setShowSpecializationDropdown(false);
                                  }}
                                >
                                  {spec.name}
                                </div>
                              ))}
                              {filteredSpecializations.length === 0 && <div className="px-3 py-2 text-gray-400 text-sm">{!formData.stream ? 'Select Stream first' : 'No results'}</div>}
                            </div>
                          )}
                        </div>

                        {/* Session - Column 2 */}
                        <div>
                          <label className={labelClass}>Session</label>
                          <input
                            type="text"
                            name="session"
                            value={formData.session || ''}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="e.g., Spring 2023, Fall 2024"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* New Row: Present Research Area, Department, Specialization - Only for Professor */}
                  {isAcademicRole && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                      {/* Present Research Area - Only for professors */}
                      <div>
                        <label className={labelClass}>Present Research Area</label>
                        <input
                          type="text"
                          name="presentResearchArea"
                          value={formData.presentResearchArea || ''}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Research area"
                        />
                      </div>

                      {/* Department */}
                      <div className="relative">
                        <label className={labelClass}>Department</label>
                        <input
                          type="text"
                          value={searchSubject}
                          onChange={(e) => {
                            setSearchSubject(e.target.value);
                            setShowSubjectDropdown(true);
                          }}
                          onFocus={() => setShowSubjectDropdown(true)}
                          onBlur={() => setTimeout(() => setShowSubjectDropdown(false), 200)}
                          className={`${inputClass} bg-white`}
                          placeholder="Search Department"
                        />
                        {showSubjectDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {masterSubjects.filter(u => u.name.toLowerCase().includes(searchSubject.toLowerCase())).map((sub) => (
                              <div
                                key={sub.id}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                onMouseDown={() => {
                                  setFormData({
                                    ...formData,
                                    subject: sub.id,
                                    stream: '',
                                    specialization: ''
                                  });
                                  setSearchSubject(sub.name);
                                  setSearchStream('');
                                  setSearchSpecialization('');
                                  // Filter streams
                                  const filteredM = masterMajors.filter(m => String(m.subject_id) === String(sub.id));
                                  setFilteredMajors(filteredM);
                                  setFilteredSpecializations([]);
                                  setShowSubjectDropdown(false);
                                }}
                              >
                                {sub.name}
                              </div>
                            ))}
                            {masterSubjects.length === 0 && <div className="px-3 py-2 text-gray-400 text-sm">No results</div>}
                          </div>
                        )}
                      </div>

                      {/* Stream */}
                      <div className="relative">
                        <label className={labelClass}>Stream</label>
                        <input
                          type="text"
                          value={searchStream}
                          onChange={(e) => {
                            setSearchStream(e.target.value);
                            setShowStreamDropdown(true);
                          }}
                          onFocus={() => setShowStreamDropdown(true)}
                          onBlur={() => setTimeout(() => setShowStreamDropdown(false), 200)}
                          disabled={!formData.subject}
                          className={`${inputClass} bg-white disabled:bg-gray-50`}
                          placeholder="Search Stream"
                        />
                        {showStreamDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {filteredMajors.filter(u => u.name.toLowerCase().includes(searchStream.toLowerCase())).map((str) => (
                              <div
                                key={str.id}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                onMouseDown={() => {
                                  setFormData({
                                    ...formData,
                                    stream: str.id,
                                    specialization: ''
                                  });
                                  setSearchStream(str.name);
                                  setSearchSpecialization('');
                                  // Filter specs
                                  const filteredS = masterSpecializations.filter(s => String(s.stream_id) === String(str.id));
                                  setFilteredSpecializations(filteredS);
                                  setShowStreamDropdown(false);
                                }}
                              >
                                {str.name}
                              </div>
                            ))}
                            {filteredMajors.length === 0 && <div className="px-3 py-2 text-gray-400 text-sm">{!formData.subject ? 'Select Dept first' : 'No results'}</div>}
                          </div>
                        )}
                      </div>

                      {/* Specialization */}
                      <div className="relative">
                        <label className={labelClass}>Specialization</label>
                        <input
                          type="text"
                          value={searchSpecialization}
                          onChange={(e) => {
                            setSearchSpecialization(e.target.value);
                            setShowSpecializationDropdown(true);
                          }}
                          onFocus={() => setShowSpecializationDropdown(true)}
                          onBlur={() => setTimeout(() => setShowSpecializationDropdown(false), 200)}
                          disabled={!formData.stream}
                          className={`${inputClass} bg-white disabled:bg-gray-50`}
                          placeholder="Search Specialization"
                        />
                        {showSpecializationDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {filteredSpecializations.filter(u => u.name.toLowerCase().includes(searchSpecialization.toLowerCase())).map((spec) => (
                              <div
                                key={spec.id}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                onMouseDown={() => {
                                  setFormData({ ...formData, specialization: spec.id });
                                  setSearchSpecialization(spec.name);
                                  setShowSpecializationDropdown(false);
                                }}
                              >
                                {spec.name}
                              </div>
                            ))}
                            {filteredSpecializations.length === 0 && <div className="px-3 py-2 text-gray-400 text-sm">{!formData.stream ? 'Select Stream first' : 'No results'}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Address Information - OPTIONAL */}
                <div className="p-4 bg-gradient-to-br from-red-50/20 to-pink-50/20 border-b-2 border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Address Information
                      </h2>
                      <p className="text-gray-500 text-xs">Where do you live?</p>
                    </div>
                  </div>

                  {/* Current Address */}
                  <div className="bg-white rounded-xl p-3 mb-3 shadow-sm">
                    <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      Current Address <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className={labelClass}>Address Line 1</label>
                        <input
                          type="text"
                          name="currentAddressLine1"
                          value={formData.currentAddressLine1}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="House/Flat No"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Address Line 2</label>
                        <input
                          type="text"
                          name="currentAddressLine2"
                          value={formData.currentAddressLine2}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Area, Landmark"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className={labelClass}>City</label>
                        <input
                          type="text"
                          name="currentCity"
                          value={formData.currentCity}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="City"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>State</label>
                        <input
                          type="text"
                          name="currentState"
                          value={formData.currentState}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="State"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>PIN</label>
                        <input
                          type="text"
                          name="currentPinCode"
                          value={formData.currentPinCode}
                          onChange={handleChange}
                          maxLength="6"
                          className={inputClass}
                          placeholder="PIN"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Country</label>
                        <input
                          type="text"
                          name="currentCountry"
                          value={formData.currentCountry}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Same as Permanent Address Checkbox */}
                  <div className="mb-3 flex justify-center">
                    <label className="flex items-center space-x-2 cursor-pointer bg-white px-4 py-2 rounded-xl shadow-sm hover:shadow-md transition-all">
                      <input
                        type="checkbox"
                        checked={sameAsPermanent}
                        onChange={handleSameAsPermanent}
                        className="w-4 h-4 text-primary-blue focus:ring-primary-blue border-gray-300 rounded"
                      />
                      <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                        <FaCheckCircle className="text-green-500 text-sm" />
                        Permanent Address Same as Current Address
                      </span>
                    </label>
                  </div>

                  {/* Permanent Address */}
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                      Permanent Address <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        {/* Removed required asterisk */}
                        <label className={labelClass}>Address Line 1</label>
                        <input
                          type="text"
                          name="permanentAddressLine1"
                          value={formData.permanentAddressLine1}
                          onChange={handleChange}
                          disabled={sameAsPermanent}
                          /* Removed required attribute */
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-500`}
                          placeholder="House/Flat No"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Address Line 2</label>
                        <input
                          type="text"
                          name="permanentAddressLine2"
                          value={formData.permanentAddressLine2}
                          onChange={handleChange}
                          disabled={sameAsPermanent}
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-500`}
                          placeholder="Area, Landmark"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        {/* Removed required asterisk */}
                        <label className={labelClass}>City</label>
                        <input
                          type="text"
                          name="permanentCity"
                          value={formData.permanentCity}
                          onChange={handleChange}
                          disabled={sameAsPermanent}
                          /* Removed required attribute */
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-500`}
                          placeholder="City"
                        />
                      </div>

                      <div>
                        {/* Removed required asterisk */}
                        <label className={labelClass}>State</label>
                        <input
                          type="text"
                          name="permanentState"
                          value={formData.permanentState}
                          onChange={handleChange}
                          disabled={sameAsPermanent}
                          /* Removed required attribute */
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-500`}
                          placeholder="State"
                        />
                      </div>

                      <div>
                        {/* Removed required asterisk */}
                        <label className={labelClass}>PIN</label>
                        <input
                          type="text"
                          name="permanentPinCode"
                          value={formData.permanentPinCode}
                          onChange={handleChange}
                          maxLength="6"
                          disabled={sameAsPermanent}
                          /* Removed required attribute */
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-500`}
                          placeholder="PIN"
                        />
                      </div>

                      <div>
                        {/* Removed required asterisk */}
                        <label className={labelClass}>Country</label>
                        <input
                          type="text"
                          name="permanentCountry"
                          value={formData.permanentCountry}
                          onChange={handleChange}
                          disabled={sameAsPermanent}
                          /* Removed required attribute */
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-500`}
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </div>
                </div>



                {/* Education History - OPTIONAL - Hidden for Admin/User */}
                {!['admin', 'user'].includes(userRole) && (
                  <div className="p-4 bg-gradient-to-br from-purple-50/30 to-pink-50/30 border-b-2 border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          Education History
                          {/* <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">OPTIONAL</span> */}
                        </h2>
                        <p className="text-gray-500 text-xs">Add qualifications</p>
                      </div>
                    </div>

                    {educationEntries.map((entry, index) => (
                      <div key={entry.id} className="mb-3 p-3 bg-white rounded-xl shadow-sm border-l-2 border-orange-500">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1">
                            <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            Education Entry
                          </h3>
                          {educationEntries.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEducationEntry(entry.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <label className={labelClass}>Qualification</label>
                            <input
                              type="text"
                              value={entry.lastQualification}
                              onChange={(e) =>
                                handleEducationChange(entry.id, 'lastQualification', e.target.value)
                              }
                              className={inputClass}
                              placeholder="e.g., Bachelor's Degree"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Board/University</label>
                            <input
                              type="text"
                              value={entry.boardUniversity}
                              onChange={(e) =>
                                handleEducationChange(entry.id, 'boardUniversity', e.target.value)
                              }
                              className={inputClass}
                              placeholder="Board/University"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Institute</label>
                            <input
                              type="text"
                              value={entry.instituteName}
                              onChange={(e) =>
                                handleEducationChange(entry.id, 'instituteName', e.target.value)
                              }
                              className={inputClass}
                              placeholder="Institute"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Passing Year</label>
                            <input
                              type="number"
                              value={entry.passingYear}
                              onChange={(e) =>
                                handleEducationChange(entry.id, 'passingYear', e.target.value)
                              }
                              min="1950"
                              max={new Date().getFullYear()}
                              className={inputClass}
                              placeholder="Year"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className={labelClass}>Marks/CGPA</label>
                            <input
                              type="text"
                              value={entry.marksGrade}
                              onChange={(e) =>
                                handleEducationChange(entry.id, 'marksGrade', e.target.value)
                              }
                              className={inputClass}
                              placeholder="e.g., 85%"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Certificate</label>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleEducationFileChange(entry.id, 'certificate', e.target.files[0])
                              }
                              className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100"
                            />
                            {entry.certificate ? (
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <FaCheckCircle />{entry.certificate.name}
                              </p>
                            ) : entry.certificateUrl && (
                              <a href={getImageUrl(entry.certificateUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 mt-1 flex items-center gap-1 hover:underline">
                                <FaCheckCircle /> View Saved Document
                              </a>
                            )}
                          </div>

                          <div>
                            <label className={labelClass}>Marksheet</label>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleEducationFileChange(entry.id, 'marksheet', e.target.files[0])
                              }
                              className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100"
                            />
                            {entry.marksheet ? (
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <FaCheckCircle />{entry.marksheet.name}
                              </p>
                            ) : entry.marksheetUrl && (
                              <a href={getImageUrl(entry.marksheetUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 mt-1 flex items-center gap-1 hover:underline">
                                <FaCheckCircle /> View Saved Document
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addEducationEntry}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md font-semibold text-sm"
                    >
                      <FaPlus className="text-xs" />
                      Add Education
                    </button>
                  </div>
                )}

                {/* Achievement Section - Only for assigned professors/academic */}
                {isAcademicRole && (
                  <div className="p-4 border-b-2 border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <FaTrophy className="text-yellow-500" />
                          About Achievement
                        </h2>
                        <p className="text-gray-500 text-xs">Awards and recognitions</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className={labelClass}>Award / Certificate / Accolades</label>
                        <input
                          type="text"
                          name="awardCertificate"
                          value={formData.awardCertificate}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="e.g., Best Researcher Award"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Year</label>
                        <input
                          type="number"
                          name="awardYear"
                          value={formData.awardYear}
                          onChange={handleChange}
                          min="1950"
                          max={new Date().getFullYear()}
                          className={inputClass}
                          placeholder="Year"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Awarded By</label>
                        <input
                          type="text"
                          name="awardedBy"
                          value={formData.awardedBy}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Organization/Institution"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Note</label>
                        <textarea
                          name="achievementNote"
                          value={formData.achievementNote}
                          onChange={handleChange}
                          className={`${inputClass} resize-none`}
                          placeholder="Additional details"
                          rows="1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Designation in Paper Portal - For Everyone (Academic + Admin/User) */}
                {showBankingAndAchievement && (
                  <div className="p-4 border-b-2 border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <FaBriefcase className="text-blue-500" />
                          Designation in Paper Portal
                        </h2>
                        <p className="text-gray-500 text-xs">Professional information</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className={labelClass}>Designation</label>
                        <input
                          type="text"
                          name="portalDesignation"
                          value={formData.portalDesignation}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Your designation"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Date of Joining</label>
                        <input
                          type="date"
                          name="portalJoiningDate"
                          value={formData.portalJoiningDate}
                          onChange={handleChange}
                          className={inputClass}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Last Company Worked For</label>
                        <input
                          type="text"
                          name="lastCompany"
                          value={formData.lastCompany}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Company name"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Total Experience</label>
                        <input
                          type="text"
                          name="totalExperience"
                          value={formData.totalExperience}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="e.g., 5 years"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Banking Information - Only for assigned professors, admin, and users */}
                {showBankingAndAchievement && (
                  <div className="p-4 bg-gradient-to-br from-green-50/30 to-emerald-50/30 border-b-2 border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          Banking Information
                        </h2>
                        <p className="text-gray-500 text-xs">Payment and banking details</p>
                      </div>
                    </div>

                    {isAcademicRole ? (
                      /* Professor Banking - Two 4-column grids */
                      <>
                        {/* Row 1: Review Consultancy, Bank Name, IFSC, Account Number */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <label className={labelClass}>Review Consultancy Charges</label>
                            <input
                              type="text"
                              name="reviewConsultancyCharges"
                              value={formData.reviewConsultancyCharges}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="e.g., ₹5000"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Bank Name</label>
                            <input
                              type="text"
                              name="bankName"
                              value={formData.bankName}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Bank name"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>IFSC Code</label>
                            <input
                              type="text"
                              name="ifscCode"
                              value={formData.ifscCode}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="IFSC Code"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Account Number</label>
                            <input
                              type="text"
                              name="accountNumber"
                              value={formData.accountNumber}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Account number"
                            />
                          </div>
                        </div>

                        {/* Row 2: Branch, UPI ID, PhonePe, GPay */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <label className={labelClass}>Branch</label>
                            <input
                              type="text"
                              name="branch"
                              value={formData.branch}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Branch name"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>UPI ID</label>
                            <input
                              type="text"
                              name="upiId"
                              value={formData.upiId}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="username@bank"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>PhonePe Number</label>
                            <input
                              type="tel"
                              name="phonePayNumber"
                              value={formData.phonePayNumber}
                              onChange={handleChange}
                              maxLength="10"
                              className={inputClass}
                              placeholder="10-digit"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>GPay Number</label>
                            <input
                              type="tel"
                              name="gPayNumber"
                              value={formData.gPayNumber}
                              onChange={handleChange}
                              maxLength="10"
                              className={inputClass}
                              placeholder="10-digit"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Admin/User Banking - Original layout without Review Consultancy */
                      <>
                        {/* Bank Details Row 1 */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <label className={labelClass}>Bank Name</label>
                            <input
                              type="text"
                              name="bankName"
                              value={formData.bankName}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Bank name"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>IFSC Code</label>
                            <input
                              type="text"
                              name="ifscCode"
                              value={formData.ifscCode}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="IFSC Code"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Account Number</label>
                            <input
                              type="text"
                              name="accountNumber"
                              value={formData.accountNumber}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Account number"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Branch</label>
                            <input
                              type="text"
                              name="branch"
                              value={formData.branch}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="Branch name"
                            />
                          </div>
                        </div>

                        {/* Digital Payment Row 2 */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className={labelClass}>UPI ID</label>
                            <input
                              type="text"
                              name="upiId"
                              value={formData.upiId}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="username@bank"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>PhonePe Number</label>
                            <input
                              type="tel"
                              name="phonePayNumber"
                              value={formData.phonePayNumber}
                              onChange={handleChange}
                              maxLength="10"
                              className={inputClass}
                              placeholder="10-digit"
                            />
                          </div>

                          <div>
                            <label className={labelClass}>GPay Number</label>
                            <input
                              type="tel"
                              name="gPayNumber"
                              value={formData.gPayNumber}
                              onChange={handleChange}
                              maxLength="10"
                              className={inputClass}
                              placeholder="10-digit"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Salary Structure - OPTIONAL - Only for employees/users and only when filled by admin */}
                {(userRole === 'user' || userRole === 'admin') && salaryStructureFilled && (
                  <div className="p-4 bg-gradient-to-br from-indigo-50/30 to-purple-50/30 border-b-2 border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>{getNextSectionNumber()}</div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          Salary Structure
                          {/* <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">OPTIONAL</span> */}
                        </h2>
                        <p className="text-gray-500 text-xs">Compensation details</p>
                      </div>
                    </div>

                    {/* Row 1: Basic, HRA, DA, Perks & Facilities, CTC */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                      <div>
                        <label className={labelClass}>Basic</label>
                        <input
                          type="text"
                          name="basicSalary"
                          value={formData.basicSalary}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>HRA</label>
                        <input
                          type="text"
                          name="hra"
                          value={formData.hra}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>DA</label>
                        <input
                          type="text"
                          name="da"
                          value={formData.da}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Perks & Facilities</label>
                        <input
                          type="text"
                          name="perksFacilities"
                          value={formData.perksFacilities}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>CTC</label>
                        <input
                          type="text"
                          name="ctc"
                          value={formData.ctc}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>
                    </div>

                    {/* Row 2: Deduction, PF, Medical, LI, Advanced IT Deduction */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <div>
                        <label className={labelClass}>Deduction</label>
                        <input
                          type="text"
                          name="deduction"
                          value={formData.deduction}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>PF</label>
                        <input
                          type="text"
                          name="pf"
                          value={formData.pf}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Medical</label>
                        <input
                          type="text"
                          name="medical"
                          value={formData.medical}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>LI</label>
                        <input
                          type="text"
                          name="li"
                          value={formData.li}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Advanced IT Deduction</label>
                        <input
                          type="text"
                          name="advancedItDeduction"
                          value={formData.advancedItDeduction}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="₹ Amount"
                          disabled={userRole === 'user'}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload Documents - OPTIONAL */}
                <div className="p-4 bg-gradient-to-br from-yellow-50/30 to-orange-50/30">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`${sectionBadgeClass}`} style={darkGradient}>
                      {getNextSectionNumber()}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Upload Personal Documents
                        {/* <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">OPTIONAL</span> */}
                      </h2>
                      <p className="text-gray-500 text-xs">Identity proof and profile picture</p>
                    </div>
                  </div>

                  {/* Profile Picture Upload */}
                  <div className="mb-4 p-3 bg-white rounded-xl shadow-sm">
                    <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1">
                      <FaUser className="text-blue-500" />
                      Profile Picture
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="relative group">
                        <div className="w-28 h-28 rounded-2xl border-2 border-primary-blue overflow-hidden bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center shadow-md">
                          {profileImage || profileImageUrl ? (
                            <img
                              key={profileImage || profileImageUrl}
                              src={getImageUrl(profileImage || profileImageUrl)}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <FaUser className="text-4xl text-gray-400" />
                          )}
                        </div>
                        <label className="absolute bottom-0 right-0 text-white p-2 rounded-lg cursor-pointer hover:scale-110 transition-all shadow-lg" style={darkGradient}>
                          <FaCamera className="text-sm" />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleProfileImageChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-2">Upload your profile picture</p>
                        <p className="text-xs text-gray-500">Recommended: Square image, at least 400x400px</p>
                        {profileImage && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <FaCheckCircle /> Profile picture uploaded
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar Documents */}
                  <div className="p-3 bg-white rounded-xl shadow-sm">
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Aadhaar Documents</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className={labelClass}>Aadhaar Number/ Masked Aadhaar Number</label>
                        <input
                          type="text"
                          name="aadhaarNumber"
                          value={formData.aadhaarNumber}
                          onChange={handleChange}
                          maxLength="12"
                          className={inputClass}
                          placeholder="12-digit"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Aadhaar Front / Masked Aadhaar Front</label>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setAadhaarFront(e.target.files[0])}
                          className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100"
                        />
                        {aadhaarFront ? (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <FaCheckCircle />{aadhaarFront.name}
                          </p>
                        ) : documentUrls.aadhaarFrontUrl && (
                          <a href={getImageUrl(documentUrls.aadhaarFrontUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 mt-1 flex items-center gap-1 hover:underline">
                            <FaCheckCircle /> View Saved Document
                          </a>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>Aadhaar Back / Masked Aadhaar Back</label>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setAadhaarBack(e.target.files[0])}
                          className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100"
                        />
                        {aadhaarBack ? (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <FaCheckCircle />{aadhaarBack.name}
                          </p>
                        ) : documentUrls.aadhaarBackUrl && (
                          <a href={getImageUrl(documentUrls.aadhaarBackUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 mt-1 flex items-center gap-1 hover:underline">
                            <FaCheckCircle /> View Saved Document
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="p-4 flex justify-center rounded-b-2xl" style={darkGradient}>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex items-center gap-2 px-12 py-3 bg-white text-indigo-700 text-base font-bold rounded-xl hover:shadow-xl transform hover:scale-105 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-indigo-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="text-lg" />
                        Save Profile
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div >
          )}
        </div >
      </div >
    </>
  );
};

export default Profile;